import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useLang, pick } from "../../lib/i18n";

const SOURCE_STYLES = {
  Tripadvisor: "bg-[#00AA6C]",
  "Google Review": "bg-[#4285F4]",
  "Booking.com": "bg-[#003B95]",
};

export default function Reviews({ reviews }) {
  const { t, lang } = useLang();
  const [index, setIndex] = useState(0);
  const list = reviews || [];

  useEffect(() => {
    if (list.length < 2) return;
    const t2 = setInterval(() => setIndex((i) => (i + 1) % list.length), 7000);
    return () => clearInterval(t2);
  }, [list.length]);

  if (!list.length) return null;
  const review = list[index];

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 sm:py-32 bg-[#F4EFEA] overflow-hidden">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-xs uppercase tracking-[0.3em] font-sans font-semibold text-[#9A7B66] mb-5"
        >
          {t("reviews.eyebrow")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-serif font-normal tracking-tight text-[#2C2825] leading-tight text-3xl sm:text-4xl lg:text-5xl"
        >
          {t("reviews.title")}
        </motion.h2>

        <div className="relative mt-14 min-h-[260px]">
          <Quote className="mx-auto w-10 h-10 text-[#9A7B66]/40 mb-6" />
          <AnimatePresence mode="wait">
            <motion.figure
              key={review.id || index}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.6 }}
            >
              <blockquote
                data-testid="review-text"
                className="font-serif italic font-light text-2xl sm:text-3xl text-[#2C2825] leading-relaxed"
              >
                "{pick(review, "text", lang)}"
              </blockquote>
              <figcaption className="mt-8">
                <div className="flex items-center justify-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${i < (review.rating || 5) ? "fill-[#9A7B66] text-[#9A7B66]" : "text-[#D3C8B9]"}`}
                    />
                  ))}
                </div>
                <div className="font-sans font-semibold text-sm text-[#2C2825]">{review.guest_name}</div>
                <div className="text-xs text-[#A0988E] font-sans mt-0.5">{review.location}</div>
                <span
                  data-testid="review-source-badge"
                  className={`inline-flex mt-3 px-3 py-1 rounded-full text-[11px] font-sans font-semibold text-white ${
                    SOURCE_STYLES[review.source] || "bg-[#9A7B66]"
                  }`}
                >
                  {t("reviews.via")} {review.source}
                </span>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>

        {list.length > 1 && (
          <div className="mt-10 flex items-center justify-center gap-5">
            <button
              data-testid="review-prev-button"
              onClick={() => setIndex((index - 1 + list.length) % list.length)}
              className="p-2.5 rounded-full border border-[#9A7B66]/30 text-[#9A7B66] hover:bg-[#9A7B66] hover:text-white transition-colors"
              aria-label="Previous review"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              {list.map((_, i) => (
                <button
                  key={i}
                  data-testid={`review-dot-${i}`}
                  onClick={() => setIndex(i)}
                  aria-label={`Review ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === index ? "w-7 bg-[#9A7B66]" : "w-2.5 bg-[#9A7B66]/30"
                  }`}
                />
              ))}
            </div>
            <button
              data-testid="review-next-button"
              onClick={() => setIndex((index + 1) % list.length)}
              className="p-2.5 rounded-full border border-[#9A7B66]/30 text-[#9A7B66] hover:bg-[#9A7B66] hover:text-white transition-colors"
              aria-label="Next review"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
